import {
  channelInfo,
  channelLegendRichInfo,
  dealwithChannelChartKey,
} from '@op/components/ChannelLabel';
import {
  formatChartParamsDate,
  textShowElement,
  textShowImgElement,
} from '@op/components/ChartCommon/formatter';
import EChartCommon from '@op/components/EChartCommon';
import useLanguage from '@op/hooks/useLanguage';
import { DBO_V } from '@op/utils/format';
import { dealWithOthersName } from '@op/utils/languageValue';
import { formatTimeToShow } from '@op/utils/time';
import React, { useMemo } from 'react';

export default function CommonPieChart({
  eventsDict,
  pieData,
  type = 'channel',
  filter,
  metricItem,
}: {
  eventsDict: any;
  pieData: any;
  type: 'channel' | 'language';
  filter: any;
  metricItem: { text: string; format: (value: any) => any };
}) {
  const { formatMessage } = useLanguage();
  const eChartsRef = React.useRef(null as any);
  const createPieChartOptions = () => {
    const { legend, data, total } = pieData;
    const { text, format = (value) => DBO_V.reviews(value) } = metricItem;
    return {
      legend: {
        data: legend,
        formatter: (value: any) => {
          if (type === 'channel') {
            return `{${dealwithChannelChartKey(
              value,
            )}|} {name|${dealWithOthersName(value, 'channel')}}`;
          }
          return dealWithOthersName(value, 'language');
        },
      },
      tooltip: {
        borderWidth: 0,
        borderRadius: 6,
        padding: 0,
        formatter: (params: any) =>
          formatChartParamsDate({
            params,
            title: formatTimeToShow(filter),
            rightTitle: formatMessage({ id: text }),
            listTitleFunc: (item) => item.name,
            textShowFun: (marker: string, seriesName: string) => {
              if (type === 'channel') {
                const { name, img } = channelInfo(
                  seriesName.toLocaleLowerCase() as any,
                );
                return textShowImgElement(
                  marker,
                  dealWithOthersName(name, 'channel'),
                  img,
                );
              }

              return textShowElement(
                marker,
                dealWithOthersName(seriesName, 'language'),
              );
            },
            valueFormat: (value) => {
              const percent = DBO_V.percentage(params?.value, total, 2);
              return `${isNaN(value) ? '-' : format(value)}(${percent})`;
            },
          }),
      },
      title: [
        {
          text: `{name|${format(total)}}`,
          top: '43%',
          left: 'center',
          textStyle: {
            rich: {
              name: {
                fontSize: 24,
                fontFamily: 'num-m',
                color: '#000000',
                padding: [10, 0],
              },
            },
          },
        },
      ],
      series: [
        {
          data,
          label: {
            color: '#262832',
            alignTo: 'edge',
            formatter: (params: any) => {
              const { name, value } = params || {};
              const percent = DBO_V.percentage(value, total, 2);
              let labelText = '';
              if (type === 'channel') {
                labelText = `{${dealwithChannelChartKey(name)}|}`;
              }
              return `${labelText}{value| ${dealWithOthersName(
                name,
                type,
              )}}: {a|${DBO_V.reviews(value)}${
                !value ? '' : `(${percent})`
              }}\n{hr|}\n{a|}`;
            },
            edgeDistance: 10,
            rich: {
              a: {
                // color: '#999',
                lineHeight: 20, // 设置最后一行空数据高度，为了能让延长线与hr线对接起来
                align: 'center',
              },
              hr: {
                // 设置hr是为了让中间线能够自适应长度
                borderColor: 'auto', // hr的颜色为auto时候会主动显示颜色的
                width: '105%',
                borderWidth: 0.5,
                height: 0.5,
              },
              per: {
                // 用百分比数据来调整下数字位置，显的好看些。如果不设置，formatter最后一行的空数据就不需要
                padding: [4, 0],
              },
              ...channelLegendRichInfo(16, 16),
            },
          },
          labelLayout: (params: any) => {
            const isLeft =
              params.labelRect.x <
              eChartsRef?.current?.getEchartsInstance?.()?.getWidth() / 2;
            const points = params.labelLinePoints;
            // Update the end point.
            points[2][0] = isLeft
              ? params.labelRect.x
              : params.labelRect.x + params.labelRect.width;
            return {
              labelLinePoints: points,
            };
          },
          emphasis: {
            focus: 'self',
            label: {
              opacity: 0.2,
            },
            labelLine: {
              lineStyle: {
                opacity: 0.2,
              },
            },
          },
          blur: {
            label: { opacity: 0.2 },
            itemStyle: {
              opacity: 0.2,
            },
            labelLine: {
              lineStyle: {
                opacity: 0.2,
              },
            },
          },
        },
      ],
      graphic: [
        {
          type: 'text',
          left: 'center',
          top: '34%',
          style: {
            fill: '#444444',
            fontSize: 12,
          },
        },
      ],
    };
  };

  const { options, isEmpty } = useMemo(() => {
    if (pieData === null || pieData.isEmpty) {
      return { options: null, isEmpty: true };
    }

    return { options: createPieChartOptions(), isEmpty: false };
  }, [pieData, formatMessage]);

  return (
    <EChartCommon
      optionsProps={{ type: 'pie' }}
      height={338}
      options={options}
      empty={isEmpty}
      ref={eChartsRef}
      loading={false}
      eventsDict={eventsDict}
    ></EChartCommon>
  );
}
